import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MultiSelectOption } from '../../../components/ui/WizardMultiSelectChips';
import type { LookupItem } from '../../../mocks/criminalMasterData';
import * as civilAppellateService from '../../../services/civilAppellateCaseRequests';
import { isCivilAppellateCategory, setCivilAppellateCategoryId } from './civilAppellateConstants';

export function useCaseCategorization(
  fileCategoryId: string,
  subFileCategoryId: string,
  options?: { enabled?: boolean },
) {
  const enabled = options?.enabled ?? true;
  const [fileCategories, setFileCategories] = useState<LookupItem[]>([]);
  const [subCategories, setSubCategories] = useState<LookupItem[]>([]);
  const [applicableAreas, setApplicableAreas] = useState<LookupItem[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(enabled);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [loadingAreas, setLoadingAreas] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setLoadingCategories(false);
      return;
    }

    let mounted = true;
    setLoadingCategories(true);

    void civilAppellateService.getFileCategories().then((r) => {
      if (!mounted) return;
      const categories = r.success ? r.data : [];
      const match = categories.find((c) => isCivilAppellateCategory(c));
      if (match?.id) setCivilAppellateCategoryId(match.id);
      setFileCategories(categories);
      setLoadingCategories(false);
    });

    return () => {
      mounted = false;
    };
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !fileCategoryId) {
      setSubCategories([]);
      setLoadingSubCategories(false);
      return;
    }

    let mounted = true;
    setLoadingSubCategories(true);

    void civilAppellateService.getSubFileCategories(fileCategoryId).then((r) => {
      if (!mounted) return;
      setSubCategories(r.success ? r.data : []);
      setLoadingSubCategories(false);
    });

    return () => {
      mounted = false;
    };
  }, [enabled, fileCategoryId]);

  useEffect(() => {
    if (!enabled || !subFileCategoryId) {
      setApplicableAreas([]);
      setLoadingAreas(false);
      return;
    }

    let mounted = true;
    setLoadingAreas(true);

    void civilAppellateService.getSubCategoryApplicableAreas(subFileCategoryId).then((r) => {
      if (!mounted) return;
      setApplicableAreas(r.success ? r.data : []);
      setLoadingAreas(false);
    });

    return () => {
      mounted = false;
    };
  }, [enabled, subFileCategoryId]);

  const fileCategoryName = useMemo(
    () => fileCategories.find((c) => c.id === fileCategoryId)?.name ?? '',
    [fileCategories, fileCategoryId],
  );

  const subFileCategoryName = useMemo(
    () => subCategories.find((s) => s.id === subFileCategoryId)?.name ?? '',
    [subCategories, subFileCategoryId],
  );

  const applicableAreaOptions: MultiSelectOption[] = useMemo(
    () => applicableAreas.map((a) => ({ id: a.id, label: a.name })),
    [applicableAreas],
  );

  const resolveAreaLabels = useCallback(
    (ids: string[]) =>
      ids.map((id) => applicableAreas.find((a) => a.id === id)?.name ?? id).filter(Boolean),
    [applicableAreas],
  );

  return {
    fileCategories,
    subCategories,
    applicableAreaOptions,
    fileCategoryName,
    subFileCategoryName,
    resolveAreaLabels,
    loadingCategories,
    loadingSubCategories,
    loadingAreas,
  };
}
